// The Sales app's home: the team's forecast, pipeline and leads, one tab at a
// time under a metric row. Everything is team-wide (a co-selling team). The header's
// Open My Dashboard opens the viewer's favorite reports (/reports/dashboard/favorites; MOTHER-294).
import {
  AttentionList, BarList, BulletColumnChart, Button, ChartCard, ChartHoverRows, ChartLegend, InlineMessage, MetricRow, OptionSelect,
  PageContent, PageHeader, Spinner, SummaryTable, Tab, TabList, Tabs, TargetIcon, TextLink, Card,
  type BulletColumnItem, type SummaryTableRow,
} from '@olytic/foundation';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { authenticatedFetch } from './auth/authClient';
import { longDate } from './delivery/model';
import { useDocumentTitle } from './documentTitle';
import {
  attentionDeals, conversion, dateLabel, fetchForecast, fetchForecastRows, fetchLeads, fetchPipeline, forecastRows, isOpenLead, leadName,
  leadsByMonth, localToday, money, monthLabel, NO_SELECTION, ofTarget, RANGES, rangeWindow, selectionTitle, sourceLabel, stageLabel,
  stageRollup, sumOf, toggleSelection, weightedOf,
  type ForecastMonth, type Lead, type OpportunityMonth, type Pipeline, type RangeKey, type Selection,
} from './home/salesHome';
import { fetchSalesPulse, k, pulseTiles, type SalesPulse } from './home/salesPulse';
import { MY_DASHBOARD_ROUTE } from './navigation';
import { recordPath } from './objects/routes';

type TabKey = 'forecast' | 'pipeline' | 'leads';
const TABS: readonly [TabKey, string][] = [['forecast', 'Forecast'], ['pipeline', 'Pipeline'], ['leads', 'Leads']];
const TAB_KEY = 'mothership.sales.homeTab';

function rememberedTab(): TabKey {
  try {
    const v = window.localStorage.getItem(TAB_KEY);
    if (TABS.some(([key]) => key === v)) return v as TabKey;
  } catch {
    // Private windows: start on Forecast.
  }
  return 'forecast';
}

const pct = (n: number) => `${n}%`;
const deals = (n: number) => `${n} ${n === 1 ? 'deal' : 'deals'}`;
const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/** Loads once per key; null until it arrives. */
function useLoad<T>(load: () => Promise<T>, deps: unknown[], fallback: string): { data: T | null; error: string | null } {
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
  useEffect(() => {
    let live = true;
    setState({ data: null, error: null });
    load()
      .then((data) => live && setState({ data, error: null }))
      .catch((e: unknown) => live && setState({ data: null, error: errorText(e, fallback) }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function Loading({ label }: { label: string }) {
  return <div className="flex justify-center py-10"><Spinner label={label} /></div>;
}

function RecordLink({ to, children }: { to: string; children: ReactNode }) {
  return <TextLink asChild><Link to={to}>{children}</Link></TextLink>;
}

export function HomePage(): JSX.Element {
  useDocumentTitle('Sales Pulse');
  const [tab, setTab] = useState<TabKey>(rememberedTab);
  const pulse = useLoad<SalesPulse>(() => fetchSalesPulse(authenticatedFetch), [], 'Could not load the sales numbers.');
  const pipeline = useLoad<Pipeline>(() => fetchPipeline(authenticatedFetch), [], 'Could not load the pipeline.');
  const today = pipeline.data?.today ?? localToday();

  const pickTab = (key: string) => {
    setTab(key as TabKey);
    try {
      window.localStorage.setItem(TAB_KEY, key);
    } catch {
      // Not remembered; the tab still changes.
    }
  };

  let body: ReactNode;
  if (tab === 'forecast') body = <ForecastTab today={today} metrics={<SalesMetrics pulse={pulse} />} />;
  else if (tab === 'pipeline') body = <PipelineTab pipeline={pipeline} />;
  else body = <LeadsTab />;

  return (
    <PageContent as="section" width="fill" className="gap-4 px-3 pb-8 pt-5 sm:px-6">
      <Card className="shrink-0 overflow-hidden">
        <PageHeader
          className="border-b-0"
          leading={<TargetIcon className="size-[46px] text-[var(--brand-accent)]" />}
          title="Sales Pulse"
          subtitle={longDate(today)}
          actions={<Button asChild variant="neutral"><Link to={MY_DASHBOARD_ROUTE}>Open My Dashboard</Link></Button>}
        />
        <Tabs value={tab} onValueChange={pickTab}>
          <TabList className="border-b-0 px-6">
            {TABS.map(([key, label]) => <Tab key={key} value={key}>{label}</Tab>)}
          </TabList>
        </Tabs>
      </Card>
      {body}
    </PageContent>
  );
}

function SalesMetrics({ pulse }: { pulse: { data: SalesPulse | null; error: string | null } }) {
  const navigate = useNavigate();
  if (pulse.error) return <InlineMessage tone="error" role="alert">{pulse.error}</InlineMessage>;
  return (
    <MetricRow
      aria-label="Sales this month"
      surface="inverse"
      metrics={(pulse.data ? pulseTiles(pulse.data) : PLACEHOLDER_TILES).map((t) => ({
        key: t.key,
        label: t.label,
        value: pulse.data ? t.value : '—',
        sub: pulse.data ? t.caption : ' ',
        ...(pulse.data ? { href: t.href, onSelect: () => navigate(t.href) } : {}),
      }))}
    />
  );
}

const PLACEHOLDER_TILES = [
  'Weighted pipeline', 'Closing this month', 'Won this month', 'Revenue this month', 'Win rate', 'New leads',
].map((label) => ({ key: label, label, value: '', caption: '', href: '' }));

// ── Forecast ───────────────────────────────────────────────────────────────

function ForecastTab({ today, metrics }: { today: string; metrics: ReactNode }) {
  const [range, setRange] = useState<RangeKey>('next6');
  const [sel, setSel] = useState<Selection>(NO_SELECTION);
  const span = useMemo(() => rangeWindow(range, today), [range, today]);
  const data = useLoad<{ months: ForecastMonth[]; rows: OpportunityMonth[] }>(
    async () => {
      const [months, rows] = await Promise.all([fetchForecast(authenticatedFetch, span), fetchForecastRows(authenticatedFetch, span)]);
      return { months, rows };
    },
    [span.from, span.months],
    'Could not load the revenue forecast.',
  );

  const pickRange = (value: string) => {
    setRange(value as RangeKey);
    setSel(NO_SELECTION);
  };

  const items: BulletColumnItem[] = (data.data?.months ?? []).map((m) => {
    const total = m.won + m.open_weighted;
    const of = ofTarget(total, m.target);
    return {
      key: m.month,
      label: shortMonth(m.month),
      segments: [{ key: 'won', value: m.won }, { key: 'open', value: m.open_weighted, texture: 'hatch' }],
      target: m.target,
      valueLabel: total > 0 ? k(total) : '',
      caption: of === null ? 'No target' : `${pct(of)} of target`,
      captionShort: of === null ? '' : pct(of),
      description: `${monthLabel(m.month)}: ${money(total)} forecast${m.target ? ` of ${money(m.target)} target` : ''}`,
    };
  });

  const rows = data.data ? forecastRows(data.data.rows, sel) : [];
  const tableRows: SummaryTableRow[] = rows.map((r) => ({
    key: r.key,
    cells: {
      opportunity: <RecordLink to={recordPath('opportunities', r.opportunityId)}>{r.opportunity}</RecordLink>,
      account: r.account,
      stage: stageLabel(r.won ? 'won' : r.stage),
      probability: r.won ? '—' : r.probability === null ? '—' : pct(r.probability),
      months: String(r.months),
      revenue: money(r.amount),
      weighted: money(r.weighted),
    },
  }));
  if (rows.length) {
    tableRows.push({
      key: 'total', total: true,
      cells: { opportunity: 'Total', revenue: money(sumOf(rows, (r) => r.amount)), weighted: money(sumOf(rows, (r) => r.weighted)) },
    });
  }

  return (
    <>
      <ChartCard
        title="Revenue forecast"
        contentClassName="flex flex-col gap-4"
        actions={(
          <>
            <ChartLegend
              items={[
                { label: 'Won', color: 'var(--chart-1)', mark: 'rect' },
                { label: 'Weighted open', color: 'var(--chart-1)', mark: 'hatch' },
                { label: 'Target', color: 'var(--brand-accent)', mark: 'target' },
              ]}
            />
            <OptionSelect
              aria-label="Time frame"
              options={RANGES.map(([value, label]) => ({ value, label }))}
              value={range}
              onValueChange={pickRange}
              className="w-44"
            />
          </>
        )}
      >
        {metrics}
        {data.error ? <InlineMessage tone="error" role="alert">{data.error}</InlineMessage>
          : !data.data ? <Loading label="Loading the forecast" />
          : (
            <BulletColumnChart
              label="Revenue forecast by month: won, weighted open and target"
              labelStyle="text"
              height={320}
              format={k}
              items={items}
              selectedKey={sel.month}
              selectedSegment={sel.part === 'all' ? null : sel.part}
              onSelect={(it) => setSel((s) => toggleSelection(s, it.key, 'all'))}
              onSelectSegment={(it, part) => setSel((s) => toggleSelection(s, it.key, part as Selection['part']))}
              tooltip={(it) => {
                const m = data.data!.months.find((x) => x.month === it.key)!;
                const total = m.won + m.open_weighted;
                const of = ofTarget(total, m.target);
                return (
                  <ChartHoverRows
                    title={monthLabel(m.month)}
                    rows={[
                      { label: 'Won', value: money(m.won) },
                      { label: 'Weighted open', value: money(m.open_weighted) },
                      { label: 'Open, unweighted', value: money(m.open_total) },
                      null,
                      { label: 'Forecast', value: money(total) },
                      ...(m.target ? [{ label: 'Target', value: money(m.target) }, { label: 'Of target', value: pct(of ?? 0) }] : []),
                    ]}
                  />
                );
              }}
            />
          )}
      </ChartCard>
      <ChartCard
        title={selectionTitle(sel, range)}
        actions={sel.month ? <Button variant="link" size="sm" onClick={() => setSel(NO_SELECTION)}>Clear selection</Button> : undefined}
      >
        {data.data ? (
          <SummaryTable
            caption="What the forecast is made of, by opportunity"
            columns={[
              { key: 'opportunity', label: 'Opportunity', wrap: true },
              { key: 'account', label: 'Account', numeric: false },
              { key: 'stage', label: 'Stage', numeric: false },
              { key: 'probability', label: 'Probability' },
              ...(sel.month ? [] : [{ key: 'months', label: 'Months' }]),
              { key: 'revenue', label: 'Revenue' },
              { key: 'weighted', label: 'Weighted' },
            ]}
            rows={tableRows}
            empty="Nothing scheduled."
          />
        ) : data.error ? null : <Loading label="Loading the revenue" />}
      </ChartCard>
    </>
  );
}

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Nov", or "Jan ’27" in January. */
const shortMonth = (m: string) => `${SHORT[Number(m.slice(5, 7)) - 1]}${m.slice(5, 7) === '01' ? ` ’${m.slice(2, 4)}` : ''}`;

// ── Pipeline ───────────────────────────────────────────────────────────────

function PipelineTab({ pipeline }: { pipeline: { data: Pipeline | null; error: string | null } }) {
  if (pipeline.error) return <InlineMessage tone="error" role="alert">{pipeline.error}</InlineMessage>;
  if (!pipeline.data) return <Loading label="Loading the pipeline" />;
  const p = pipeline.data;
  const stages = stageRollup(p);
  const open = sumOf(stages, (s) => s.amount);
  const weighted = sumOf(stages, (s) => s.weighted);
  const count = p.opportunities.length;
  const attention = attentionDeals(p);

  const oppRows: SummaryTableRow[] = p.opportunities.map((o) => ({
    key: o.id,
    cells: {
      opportunity: <RecordLink to={recordPath('opportunities', o.id)}>{o.name}</RecordLink>,
      account: o.account_id ? <RecordLink to={recordPath('accounts', o.account_id)}>{o.account_name}</RecordLink> : '—',
      stage: stageLabel(o.stage),
      amount: o.amount === null ? '—' : money(o.amount),
      probability: o.probability === null ? '—' : pct(o.probability),
      weighted: money(weightedOf(o)),
      close: o.close_date ? { text: dateLabel(o.close_date), tone: (o.close_days ?? 0) < 0 ? 'negative' : undefined } : '—',
    },
  }));
  if (count) {
    oppRows.push({ key: 'total', total: true, cells: { opportunity: 'Total', amount: money(open), weighted: money(weighted) } });
  }

  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(26rem,1fr)]">
        <ChartCard
          title="By stage"
          actions={(
            <ChartLegend
              items={[
                { label: 'Weighted', color: 'var(--chart-1)', mark: 'rect' },
                { label: 'Open', color: 'color-mix(in srgb, var(--chart-1) 28%, transparent)', mark: 'rect' },
              ]}
            />
          )}
        >
          <BarList
            layout="inline"
            items={[
              ...stages.map((s) => ({
                key: s.key,
                label: s.label,
                value: s.amount,
                part: s.weighted,
                valueLabel: s.deals.length ? `${k(s.weighted)} of ${k(s.amount)}` : '—',
                ...(s.deals.length ? { sub: deals(s.deals.length) } : {}),
              })),
              {
                key: '@total', label: 'Total', total: true, value: open, part: weighted,
                valueLabel: `${k(weighted)} of ${k(open)}`,
                sub: `${deals(count)} · ${pct(open ? Math.round((weighted / open) * 100) : 0)} weighted`,
              },
            ]}
            tooltip={(it) => {
              const s = stages.find((x) => x.key === it.key);
              const list = s ? s.deals : p.opportunities;
              return (
                <ChartHoverRows
                  title={it.label}
                  rows={[
                    ...(list.length ? list.map((o) => ({ label: o.name.length > 34 ? `${o.name.slice(0, 33)}…` : o.name, value: k(o.amount ?? 0) })) : [{ label: 'No deals', value: '' }]),
                    ...(list.length ? [null, { label: 'Open', value: money(it.value) }, { label: 'Weighted', value: money(it.part ?? 0) }] : []),
                  ]}
                />
              );
            }}
          />
        </ChartCard>
        <AttentionList
          title="Needs attention"
          layout="columns"
          items={attention.map(({ deal, due }) => ({
            id: deal.id,
            title: <RecordLink to={recordPath('opportunities', deal.id)}>{deal.name}</RecordLink>,
            meta: [deal.account_name, stageLabel(deal.stage), deal.has_quote ? null : 'no quote yet'].filter(Boolean).join(' · '),
            value: deal.amount === null ? '—' : k(deal.amount),
            due,
          }))}
          emptyTitle="Nothing waiting"
        />
      </div>
      <ChartCard title="Open opportunities">
        <SummaryTable
          caption="Open opportunities by close date"
          columns={[
            { key: 'opportunity', label: 'Opportunity', wrap: true },
            { key: 'account', label: 'Account', numeric: false },
            { key: 'stage', label: 'Stage', numeric: false },
            { key: 'amount', label: 'Amount' },
            { key: 'probability', label: 'Probability' },
            { key: 'weighted', label: 'Weighted' },
            { key: 'close', label: 'Close Date' },
          ]}
          rows={oppRows}
          empty="No open opportunities."
        />
      </ChartCard>
    </>
  );
}

// ── Leads ──────────────────────────────────────────────────────────────────

function LeadsTab() {
  const data = useLoad<{ today: string; leads: Lead[] }>(() => fetchLeads(authenticatedFetch), [], 'Could not load the leads.');
  if (data.error) return <InlineMessage tone="error" role="alert">{data.error}</InlineMessage>;
  if (!data.data) return <Loading label="Loading the leads" />;
  const { leads, today } = data.data;
  const open = leads.filter(isOpenLead);
  const months = leadsByMonth(leads, today);
  const c = conversion(leads);

  return (
    <>
      <ChartCard title="Open leads">
        <SummaryTable
          caption="Leads not yet converted or disqualified"
          columns={[
            { key: 'company', label: 'Company', wrap: true },
            { key: 'source', label: 'Source', numeric: false },
            { key: 'tier', label: 'AI Maturity', numeric: false },
            { key: 'urgency', label: 'Urgency', numeric: false },
            { key: 'created', label: 'Created' },
            { key: 'waiting', label: 'Waiting' },
          ]}
          rows={open.map((l) => ({
            key: l.id,
            cells: {
              company: <RecordLink to={recordPath('leads', l.id)}>{leadName(l)}</RecordLink>,
              source: sourceLabel(l.source),
              tier: l.maturity_tier ?? { text: '—', tone: 'muted' },
              urgency: l.urgency_score ?? { text: '—', tone: 'muted' },
              created: dateLabel(l.created_on),
              waiting: { text: `${l.days} ${l.days === 1 ? 'day' : 'days'}`, tone: l.days > 7 ? 'negative' : undefined },
            },
          }))}
          empty="No open leads. Every lead has been converted or disqualified."
        />
      </ChartCard>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(26rem,1fr)]">
        <ChartCard
          title="Leads by month"
          actions={(
            <ChartLegend
              items={[
                { label: 'Converted', color: 'var(--chart-1)', mark: 'rect' },
                { label: 'Not converted', color: 'var(--chart-1)', mark: 'hatch' },
              ]}
            />
          )}
        >
          <BulletColumnChart
            label="Leads created by month, converted and not"
            labelStyle="text"
            height={240}
            format={(v) => (Number.isInteger(v) ? String(v) : '')}
            items={months.map((m) => ({
              key: m.month,
              label: shortMonth(m.month),
              segments: [{ key: 'converted', value: m.converted }, { key: 'other', value: m.other, texture: 'hatch' }],
              valueLabel: m.converted + m.other ? String(m.converted + m.other) : '',
              description: `${monthLabel(m.month)}: ${m.converted + m.other} leads, ${m.converted} converted`,
            }))}
            tooltip={(it) => {
              const m = months.find((x) => x.month === it.key)!;
              return (
                <ChartHoverRows
                  title={monthLabel(m.month)}
                  rows={m.leads.length ? m.leads.map((l) => ({ label: leadName(l), value: stageLabel(l.status) })) : [{ label: 'No leads', value: '' }]}
                />
              );
            }}
          />
        </ChartCard>
        <ChartCard title="Conversion">
          <SummaryTable
            caption="Lead conversion"
            columns={[{ key: 'what', label: '' }, { key: 'n', label: '' }]}
            rows={[
              { key: 'leads', cells: { what: 'Leads', n: String(c.leads) } },
              { key: 'converted', cells: { what: 'Converted to opportunities', n: String(c.converted) } },
              { key: 'disqualified', cells: { what: 'Disqualified', n: String(c.disqualified) } },
              { key: 'ai', cells: { what: 'From the AI assessment', n: String(c.ai) } },
              { key: 'rate', total: true, cells: { what: 'Conversion rate', n: c.rate === null ? '—' : pct(c.rate) } },
            ]}
          />
        </ChartCard>
      </div>
    </>
  );
}
